import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../lib/utils';

const EntrepriseSidebar = ({ activeTab, setActiveTab, onLogout, stats, isOpen, onClose }) => {
  const { user } = useAuth();

  const handleSelect = (id) => {
    setActiveTab(id);
    if (onClose) onClose();
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Tableau de bord',
      icon: '📊',
      description: 'Vue d\'ensemble',
      category: 'main'
    },
    {
      id: 'employes',
      label: 'Employés',
      icon: '👥',
      description: 'Gestion des employés',
      badge: stats?.nb_employes?.toString(),
      category: 'gestion'
    },
    {
      id: 'demandes',
      label: 'Paiements',
      icon: '💳',
      description: 'Historique des paiements',
      badge: '2',
      badgeColor: 'bg-yellow-500',
      category: 'gestion'
    },
    {
      id: 'primes',
      label: 'Primes',
      icon: '🎁',
      description: 'Gestion des primes',
      category: 'gestion'
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: '📈',
      description: 'Statistiques détaillées',
      category: 'analyse'
    },
    {
      id: 'rapports',
      label: 'Rapports',
      icon: '📋',
      description: 'Exports & analyses',
      category: 'analyse'
    },
    {
      id: 'solde',
      label: 'Solde',
      icon: '💰',
      description: 'Gestion financière',
      category: 'finance'
    },
    {
      id: 'parametres',
      label: 'Paramètres',
      icon: '⚙️',
      description: 'Configuration',
      category: 'system'
    }
  ];

  return (
    <>
      {/* Overlay mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={onClose}
          data-testid="entreprise-sidebar-overlay"
        />
      )}

      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 h-screen flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:z-auto ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: 'linear-gradient(180deg, #7C3AED 0%, #A855F7 50%, #EC4899 100%)' }}
        data-testid="entreprise-sidebar"
      >
      {/* Logo/En-tête entreprise */}
      <div className="p-4 border-b border-white/20">
        {/* Bouton fermeture (mobile) */}
        <div className="flex justify-end lg:hidden -mt-1 -mr-1 mb-1">
          <button
            onClick={onClose}
            className="text-white hover:bg-white/10 p-2 rounded"
            data-testid="entreprise-sidebar-close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex flex-col items-center mb-4">
          <img 
            src="/logo-alo-advance.png" 
            alt="ALO ADVANCE" 
            className="w-auto max-h-24 lg:max-h-32 drop-shadow-lg"
          />
        </div>
        <div className="mb-3 text-center">
          <p className="font-medium text-white text-sm">{user?.entreprise_nom || 'ENTREPRISE'}</p>
          <p className="text-xs text-purple-100">{user?.role}</p>
        </div>
        
        {/* Info solde */}
        <div className="bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-white">Solde</span>
            <span className="font-bold text-white">
              {formatCurrency(stats?.solde || 0)}
            </span>
          </div>
        </div>
        
        {/* Bouton de déconnexion */}
        <button
          onClick={() => {
            if (window.confirm('Êtes-vous sûr de vouloir vous déconnecter ?')) {
              onLogout();
            }
          }}
          className="w-full flex items-center justify-center px-3 py-2 mt-3 text-sm font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors backdrop-blur-sm"
        >
          <span className="mr-2">🚪</span>
          Déconnexion
        </button>
      </div>

      {/* Menu de navigation */}
      <nav className="flex-1 p-2 overflow-y-auto">
        <div className="space-y-1">
          {menuItems.map((item, index) => {
            // Afficher un séparateur si la catégorie change
            const prevItem = index > 0 ? menuItems[index - 1] : null;
            const showSeparator = prevItem && prevItem.category !== item.category;
            
            return (
              <React.Fragment key={item.id}>
                {showSeparator && (
                  <div className="py-2 px-3">
                    <div className="border-t border-white/20"></div>
                  </div>
                )}
                <button
                  key={item.id}
                  onClick={() => handleSelect(item.id)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                    activeTab === item.id
                      ? 'bg-white text-purple-600 shadow-lg'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <span className="mr-3 text-lg">{item.icon}</span>
                  <div className="flex-1 text-left">
                    <div className="flex items-center justify-between">
                      <span>{item.label}</span>
                      {item.badge && (
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          activeTab === item.id ? 'bg-orange-500 text-white' : 'bg-white/20 text-white'
                        }`}>
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-0.5 ${activeTab === item.id ? 'text-purple-400' : 'text-purple-100'}`}>{item.description}</p>
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </nav>

      {/* Statut en bas */}
      <div className="p-4 border-t border-white/20">
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3">
          <div className="flex items-center">
            <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
            <span className="text-sm text-white font-medium">Entreprise active</span>
          </div>
          <p className="text-xs text-purple-100 mt-1">Toutes les fonctionnalités disponibles</p>
        </div>
      </div>
      </div>
    </>
  );
};

export default EntrepriseSidebar;